/**
 * `POST /api/production-records/scan-sheet` — read the handwritten numbers off
 * a photographed production-record sheet (PROD-1279 D24, plan 03 §F).
 *
 * Lives beside `cafeteria-production.ts` rather than inside it: that module is
 * the fill/sign client for a record, this one is a stateless read of a picture.
 * The endpoint PERSISTS NOTHING, so there is no cache to invalidate on success.
 *
 * The request types mirror the server contract exactly and are NOT imported
 * from the server (client and server deploy on their own clocks).
 */

import { useMutation } from '@tanstack/react-query'

import { type ApiError, apiFetch } from '@/api/http'
import type { ScanRowWire } from '@/lib/sheet-scan/build-request'

export interface ScanSheetImage {
  mediaType: string
  dataBase64: string
}

export interface ScanSheetRequest {
  schoolLocationId: string
  /** `YYYY-MM-DD`. */
  serviceDate: string
  /**
   * `record` is the COMBINED spreadsheet table: every editable column at once.
   * `delivery` is the chef's Orders sheet, the delivery record signed at
   * drop-off (PROD-1511): its Drop-off °F, its Received tick and its sign-off.
   */
  task: 'transport' | 'service' | 'record' | 'delivery'
  /** Campus name, e.g. "Hall Middle School" — the model checks it against the sheet. */
  contextLabel: string
  /** "Monday, September 14" — likewise. */
  serviceDateLabel: string
  images: ScanSheetImage[]
  rows: ScanRowWire[]
}

export interface ScanSheetCell {
  value: number | null
  confidence: 'high' | 'low'
}

export interface ScanSheetVerification {
  matchesExpected: boolean
  reason: string
  observedDateText: string | null
  observedLocationText: string | null
}

export interface ScanSheetResponse {
  /** One entry per REQUESTED row, in request order. */
  rows: Array<{ rowId: string; cells: Record<string, ScanSheetCell> }>
  /** One entry per REQUESTED image, in request order. */
  images: Array<{ index: number; verification: ScanSheetVerification }>
  /** Item names read off the paper that do not match any requested row. */
  unmatchedRowsSeen: string[]
  /** Raw text of the sheet's "Time reading" box, if any (the Orders sheet: its sign-off "Time:"). */
  timeReadingText: string | null
  /** The Orders sheet's "Received by (First Name):", verbatim; null or absent on every other task. */
  receivedByText?: string | null
}

/**
 * A vision call over up to six photos on cafeteria wifi. Without a client-side
 * abort the card spins forever on a stalled upload — the chef path's current
 * behaviour, and the thing this endpoint deliberately does not inherit.
 */
export const SCAN_TIMEOUT_MS = 120_000

/**
 * `AbortSignal.timeout` arrived in iOS 16 / Chrome 103. On an older cafeteria
 * tablet the scan goes out unbounded rather than not at all — the timeout is
 * the safety net, the upload is the feature.
 */
function scanTimeoutSignal(): AbortSignal | undefined {
  return typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(SCAN_TIMEOUT_MS) : undefined
}

export async function scanSheet(input: ScanSheetRequest): Promise<ScanSheetResponse> {
  return apiFetch<ScanSheetResponse>('/api/production-records/scan-sheet', {
    method: 'POST',
    body: input,
    signal: scanTimeoutSignal(),
  })
}

/**
 * `retry: false` is explicit even though TanStack's mutation default is already
 * 0: a blind retry here is a duplicated vision spend AND a duplicated prefill
 * over numbers the worker may already have corrected.
 */
export function useScanSheetMutation() {
  return useMutation<ScanSheetResponse, ApiError, ScanSheetRequest>({ mutationFn: scanSheet, retry: false })
}
